import { Divider } from '@advanza/ui'
import { fetchServices } from 'actions/services'
import Card from 'components/Card'
import ServiceCompetitionStats from 'components/serviceInfo/ServiceCompetitionStats'
import ServiceCreditLimitsInfo from 'components/serviceInfo/ServiceCreditLimitsInfo'
import ServiceMatchStats from 'components/serviceInfo/ServiceMatchStats'
import ServicePricesActive from 'components/serviceInfo/ServicePricesActive'
import ServiceRequestStats from 'components/serviceInfo/ServiceRequestStats'
import ServiceRequestStatsAll from 'components/serviceInfo/ServiceRequestStatsAll'
import ServiceRequestStatsCalendarChart from 'components/serviceInfo/ServiceRequestStatsCalendarChart'
import ServiceStatsExportButton from 'components/serviceInfo/ServiceStatsExportButton'
import ServicesSelect from 'components/services/ServicesSelect'
import Page from 'components/ui/Page'
import { useAppDispatch, useAppSelector } from 'hooks/hooks'
import { useRegionsList } from 'hooks/regionHooks'
import ServiceInterface from 'interfaces/Services'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { Navigate, useParams } from 'react-router-dom'

interface Region {
    id: number
    name: string
    is_hidden?: boolean
    is_visible?: boolean
    region_id: number
}

interface Service extends ServiceInterface {
    services_visible_regions?: Array<{
        region_id: number
        is_visible: boolean
    }>
}

const ServiceInfoPage = () => {
    const [redirect, setRedirect] = useState<string | false>(false)
    const { id } = useParams<{ id?: string }>()
    const parsedId = id ? parseInt(id, 10) : NaN
    const serviceId = isNaN(parsedId) ? undefined : parsedId
    const dispatch = useAppDispatch()
    const { services } = useAppSelector((state) => state.services).entities
    const hasFetchedServiceListing = Object.keys(services).length > 0
    const regionsList = useRegionsList('all')?.entities?.regions ?? {}
    const country = useSelector(
        (state: { countries: { countries: string[] } }) => state.countries.countries[0],
    )

    // Fetch services when component mounts
    useEffect(() => {
        dispatch(fetchServices())
    }, [dispatch])

    const mandatoryRegions = (Object.values(regionsList) as Region[]).filter(
        (region) => !region.is_hidden,
    )

    const visibleRegions = (() => {
        if (!serviceId) {
            return []
        }

        const service = Object.values(services).find(
            (s): s is Service => (s as Service).service_id === serviceId,
        )

        if (!service || !service.services_visible_regions) {
            return []
        }

        const visibleRegionIds = service.services_visible_regions
            .filter((region) => region.is_visible)
            .map((region) => region.region_id)

        return (Object.values(regionsList) as Region[]).filter((region) =>
            visibleRegionIds.includes(region.region_id),
        )
    })()

    const actualRegions = [...mandatoryRegions, ...visibleRegions]

    const onServiceChange = (services: number[] | Service[]) => {
        const serviceId =
            Array.isArray(services) && services.length > 0
                ? typeof services[0] === 'object' && 'service_id' in services[0]
                    ? services[0].service_id
                    : services[0]
                : null

        if (serviceId) {
            setRedirect(`/sales/site-info/${serviceId}`)
        } else {
            setRedirect('/sales/site-info')
        }
        setTimeout(() => setRedirect(false), 0) // Reset redirect state
    }

    if (redirect) {
        return <Navigate to={redirect as string} replace />
    }

    return (
        <Page title="Service Info">
            <div className="mx-auto">
                <div className="mb-8 pb-6 border-b-2 border-grey-500">
                    <h1 className="text-[28px] font-semibold text-bodyDark m-0">Site Info</h1>
                </div>

                {serviceId ? (
                    <>
                        <div className="mb-6 w-full [&>*]:w-full">
                            <ServicesSelect
                                value={serviceId || ''}
                                onChangeValue={onServiceChange}
                                multiple={false}
                                emptyOption="-"
                                placeholder="Select a service"
                            />
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                            <div className="bg-white border border-grey-500 rounded-xl p-6 shadow-[0_1px_3px_rgba(0,0,0,0.05)]">
                                <ServicePricesActive serviceId={serviceId} />
                            </div>
                            <div className="bg-white border border-grey-500 rounded-xl p-6 shadow-[0_1px_3px_rgba(0,0,0,0.05)]">
                                <ServiceCreditLimitsInfo serviceId={serviceId} />
                            </div>
                            <div className="bg-white border border-grey-500 rounded-xl p-6 shadow-[0_1px_3px_rgba(0,0,0,0.05)]">
                                <div className="border border-grey-500 rounded-lg p-4 mb-4">
                                    <ServiceStatsExportButton serviceId={serviceId} />
                                </div>
                                <div className="border border-grey-500 rounded-lg p-4">
                                    <h2 className="text-xl font-semibold text-bodyDark m-0 mb-4">
                                        Active Regions{' '}
                                        <span className="text-grey-800 font-normal">
                                            ({Object.keys(actualRegions).length}/
                                            {Object.keys(regionsList).length})
                                        </span>
                                    </h2>
                                    <div className="text-grey-800 text-md leading-relaxed">
                                        {country === 'NL' || country === 'BE' ? (
                                            <span>All of the regions</span>
                                        ) : (
                                            <div>
                                                {(Object.values(actualRegions) as Region[]).map(
                                                    (region, index, arr) => (
                                                        <span key={region.id}>
                                                            {region.name}
                                                            {index < arr.length - 1 ? ', ' : ''}
                                                        </span>
                                                    ),
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {hasFetchedServiceListing ? (
                            <>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                                    <Card>
                                        <ServiceCompetitionStats serviceId={serviceId} />
                                    </Card>
                                    <Card>
                                        <ServiceRequestStats serviceId={serviceId} />
                                    </Card>
                                </div>

                                <div className="bg-white border border-grey-500 rounded-xl p-6 mb-8">
                                    <ServiceRequestStatsCalendarChart serviceId={serviceId} />
                                </div>

                                <div className="bg-white border border-grey-500 rounded-xl p-6">
                                    <Card useToggle header="Match stats" icon="check">
                                        <Divider />
                                        <ServiceMatchStats serviceId={serviceId} />
                                        <Divider />
                                    </Card>
                                </div>
                            </>
                        ) : null}
                    </>
                ) : (
                    <div className="mb-6 w-full [&>*]:w-full">
                        <ServicesSelect
                            value={serviceId || ''}
                            onChangeValue={onServiceChange}
                            multiple={false}
                            emptyOption="-"
                            placeholder="Select a service"
                        />
                    </div>
                )}

                {!serviceId && (
                    <>
                        <ServiceMatchStats serviceId={0} />
                        <ServiceRequestStatsAll serviceId={0} />
                    </>
                )}
            </div>
        </Page>
    )
}

export default ServiceInfoPage
